import { PhotosetConfig } from 'components/photoset/photoset.interfaces';
import { wccModules } from 'enums/wccModules';
import { AttachmentsHelpers } from 'helpers/attachments';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import WCCAttachment from 'models/attachments/attachment';
import WCCFile from 'models/attachments/file';
import WCCImage from 'models/attachments/image';
import WCCLink from 'models/attachments/link';
import WCCVideo from 'models/attachments/video';
import WCCVimeoVideo from 'models/attachments/vimeo';
import WCCYoutubeVideo from 'models/attachments/youtube';
import { WCCFileVMConfig } from '../file/file';
import { WCCAttachmentLayoutVMTheme } from '../layouts/base/base.interfaces';
import { WCCLinkVMConfig } from '../link/link';
import './box.html';
import './box.scss';

components.preload('photoset-new', 'wcc-image', 'wcc-link', 'wcc-file', 'attachment-caption', 'fancybox-image-new');

export interface AttachmentsBoxVMConfig {
    attachments?: SubscribableOrNullableValue<Array<WCCAttachment>>
    itemWidth?: SubscribableOrNullableValue<number>
    offset?: SubscribableOrNullableValue<number>
    outerOffset?: SubscribableOrNullableValue<number>
    theme?: SubscribableOrNullableValue<WCCAttachmentLayoutVMTheme>
}

@injectable()
export default class AttachmentsBoxVM {
    id = _.uniqueId('attachments-box');

    photoset: PhotosetConfig

    videos: Subscribable<Array<WCCYoutubeVideo | WCCVimeoVideo | WCCVideo>>
    files: Subscribable<Array<WCCFile>>
    links: Subscribable<Array<WCCLink>>

    hasFilesOrLinks: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) private config: AttachmentsBoxVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo
    ) {
        const itemWidth = ko.flattenComputed(config.itemWidth);
        const offset = ko.flattenComputed(config.offset);
        const outerOffset = ko.flattenComputed(config.outerOffset);

        const attachments = ko.flattenComputed(config.attachments, []);
        const images = attachments.filter<WCCImage>(a => a.fileType() === enums.AttachmentTypes.Image.value);
        this.videos = attachments.filter(a => a.isVideoplayer());

        this.photoset = {
            source: images,
            offset,
            outerOffset,
            rowsCount: 0,
            columnWidth: itemWidth
        }

        this.videos = attachments.filter<WCCYoutubeVideo | WCCVimeoVideo | WCCVideo>(a => AttachmentsHelpers.isYouTube(a.fileType()) || AttachmentsHelpers.isVimeo(a.fileType()) || AttachmentsHelpers.isVideo(a.fileType()));
        this.files = attachments.filter<WCCFile>(a => a.isFile());
        this.links = attachments.filter<WCCLink>(a => a.isLink());

        this.hasFilesOrLinks = ko.pureComputed(() => this.files().length > 0 || this.links().length > 0);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'attachments-box': true
            }
        });
    }

    getTemplate = (attachment: WCCAttachment) => {
        if (attachment.isImage())
            return 'wcc-components-attachments-grid-image';
        else if (attachment.isLink())
            return 'wcc-components-attachments-grid-link';
        else if (attachment.isFile())
            return 'wcc-components-attachments-grid-file';
        else if (attachment.isYouTube())
            return 'wcc-components-attachments-grid-youtube';
        else if (attachment.isVimeo())
            return 'wcc-components-attachments-grid-vimeo';
    }

    getFileConfig = (file: WCCFile): WCCFileVMConfig => {
        return {
            file,
            layout: 'row',
            theme: this.config.theme
        }
    }

    getLinkConfig = (link: WCCLink): WCCLinkVMConfig => {
        return {
            link,
            layout: 'row',
            theme: this.config.theme
        }
    }
}